import { useState, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { isValidJson, isValidEmail } from 'utils/stringHelpers';
import { AceEditor } from 'modules/aceEditor';
import { selectUserProjectIsUuidBased } from 'core/userContext';
import { ExpandModes, ExpansionContextProvider } from 'utils/contexts/expansion';
import { JsonView } from 'ui/treeView/JsonView';
import { spacing, colors, body2, caption, body1Bold, borderRadius } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { SmallSegmentedControl } from 'ui/segmentedControl';
import { Toggle, TOGGLE_SIZES } from 'ui/toggle';
import { InfoBanner } from 'ui/banner';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { InputText } from 'ui/inputs';
import { UserDataLevel } from 'modules/campaignAnalytics/constants';
import {
  DATA_FEED_BANNER_TEXT,
  DATA_TEST_PREVIEW_HEADER_USER_DATA,
  DATA_TEST_JSON_TOGGLE,
  DATA_TEST_PREVIEW_HEADER_DATA_FEEDS,
} from './constants';
import { useDynamicHeatmapContext } from '../../context';
import { UserSearch } from './UserSearch';

const PreviewHeader = styled.div`
  ${body1Bold};
`;

const PreviewHeaderSubtitle = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;
export const AceEditorWrapper = styled.div<{ hasError: boolean }>`
  overflow: hidden;
  ${({ hasError }) =>
    hasError ? `outline: ${spacing.single} solid ${colors.borderDestructive};` : ''}
`;

const PreviewHeaderContainer = styled.div`
  padding: ${spacing.base};
  border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  display: flex;
  background-color: ${colors.backgroundSecondary};
  justify-content: space-between;
  > div {
    display: flex;
    align-items: center;
  }

  label {
    ${body2}
    padding: ${spacing.quarter};
    margin: 0;
  }
`;

const StyledInfoBanner = styled(InfoBanner)`
  margin-top: ${spacing.plusHalf};
`;

const DataFeedHeaderContainer = styled.div`
  padding: ${spacing.base};
  border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  justify-content: space-between;
  background-color: ${colors.backgroundSecondary};
`;

const PreviewContent = styled.div`
  padding: ${spacing.plusHalf};
  max-width: 100%;
  min-width: ${pxToRem(400)};
`;

const PreviewContainer = styled.div`
  height: 100%;
  min-width: ${pxToRem(400)};
`;

const ControlsWrapper = styled.div<{ isRestricted: boolean }>`
  ${({ isRestricted }) => !isRestricted && `padding-top: ${spacing.plusHalf}`}
`;

const InfoContainer = styled.div`
  width: 100%;
  margin-top: ${spacing.plusHalf};
  border: ${spacing.single} solid ${colors.borderPrimary};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
`;

const StyledJsonView = styled(JsonView)`
  &&& {
    .json-row {
      display: block;
      border-radius: 0;
    }
    .json-property-key {
      ${caption};
      color: ${colors.textSecondary};
      padding-bottom: 0;
      align-items: center;
    }
    .json-property-value {
      padding-top: 0;
      margin-left: 0;
    }
    .json-row:last-child {
      border-radius: 0 0 ${borderRadius.lg} ${borderRadius.lg};
    }
  }
`;

const JSONLabel = styled.label`
  && {
    cursor: pointer;
    padding: ${spacing.quarter} ${spacing.half};
  }
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: ${spacing.half};
`;

const UserButton = styled(Button)`
  flex: none;
`;

const InputForm = styled.form`
  width: 100%;
`;

const NoUser = styled.div`
  color: ${colors.textSecondary};
  background-color: ${colors.backgroundSecondary};
  padding: ${spacing.base};
  border-radius: 0 0 ${borderRadius.lg} ${borderRadius.lg};
`;

const options = {
  userData: {
    name: 'User data',
    value: 'user-data',
  },
  dataFeeds: {
    name: 'Data feeds',
    value: 'data-feeds',
  },
};

const INPUT_TEXT_USER_ID = 'preview-input-user-id';
const LOAD_BUTTON_ID = 'preview-btn-load-user';

export const UserInfoPane = () => {
  const [selectedPreview, selectPreview] = useState(options.userData.value);
  const [isRawJson, setIsRawJson] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    setUserData,
    userData,
    dataFeedContent,
    dataFeedNames,
    loadUser,
    userDataLevel,
    loadedIframe,
    setLoadedIframe,
    userId,
  } = useDynamicHeatmapContext();

  const onSelect = (value: string) => {
    selectPreview(value);
    setLoadedIframe(loadedIframe + 1);
  };
  const isUuidBased = useSelector(selectUserProjectIsUuidBased);
  const canLoadUserData = isUuidBased
    ? !!inputVal.trim() // TBD
    : isValidEmail(inputVal.trim());

  const handleUserDataChange = useCallback(debounce(setUserData, 500), []);

  return (
    <PreviewContainer ref={contentRef}>
      <PreviewContent>
        {userDataLevel === UserDataLevel.Dropdown && (
          <div>
            <UserSearch />
          </div>
        )}
        {userDataLevel === UserDataLevel.Input && (
          <InputWrapper>
            <InputForm
              // method controls what happens when the form is submitted.
              // setting it to dialog is a hack to make sure the autocomplete on the input still works,
              // because the autocomplete will only be populated when the form action completes.
              // see https://iterable.atlassian.net/browse/TNT-5428 for more context
              method="dialog"
              id="user-preview"
              onSubmit={() => {
                if (inputVal !== userId || userData === '{}') {
                  loadUser(inputVal);
                }
              }}
            >
              <InputText
                data-test={INPUT_TEXT_USER_ID}
                id={INPUT_TEXT_USER_ID}
                onChange={(val) => setInputVal(val)}
                autoComplete={'on'}
                placeholder={isUuidBased ? 'Enter user email or ID' : 'Enter user email'}
                value={inputVal}
                width="100%"
              />
            </InputForm>

            <UserButton
              type="submit"
              form="user-preview"
              data-test={LOAD_BUTTON_ID}
              isDisabled={!canLoadUserData}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              Load user
            </UserButton>
          </InputWrapper>
        )}
        {dataFeedNames?.length > 0 && (
          <ControlsWrapper isRestricted={userDataLevel === UserDataLevel.Restricted}>
            <SmallSegmentedControl
              isFullWidth={true}
              onSelect={onSelect}
              options={Object.values(options)}
              value={selectedPreview}
            />
          </ControlsWrapper>
        )}
        {selectedPreview === options.userData.value && (
          <>
            <InfoContainer>
              <PreviewHeaderContainer>
                <PreviewHeader data-test={DATA_TEST_PREVIEW_HEADER_USER_DATA}>
                  User data
                </PreviewHeader>
                {userDataLevel !== UserDataLevel.Restricted && (
                  <div>
                    <JSONLabel id="json-mode-label" htmlFor="json-mode">
                      JSON
                    </JSONLabel>
                    <Toggle
                      isActive={isRawJson}
                      id="json-mode"
                      data-test={DATA_TEST_JSON_TOGGLE}
                      labelId="json-mode-label"
                      aria-labelledby="json-mode-label"
                      onClick={() => {
                        setIsRawJson(!isRawJson);
                        setLoadedIframe(loadedIframe + 1);
                      }}
                      size={TOGGLE_SIZES.SMALL}
                    />
                  </div>
                )}
              </PreviewHeaderContainer>
              {!isRawJson && userDataLevel !== UserDataLevel.Restricted ? (
                <>
                  <ExpansionContextProvider
                    localKey="heatmap-user-data"
                    initialMode={ExpandModes.collapse}
                  >
                    {userData === '{}' || !isValidJson(userData) ? (
                      <NoUser>No user loaded</NoUser>
                    ) : (
                      <StyledJsonView
                        src={JSON.parse(userData)}
                        tableColor={colors.backgroundSecondary}
                        altRowColorDepth={1}
                        showBranchFocus={true}
                      />
                    )}
                  </ExpansionContextProvider>
                </>
              ) : (
                <AceEditorWrapper hasError={!isValidJson(userData)}>
                  <AceEditor
                    editorProps={{ $blockScrolling: Infinity }}
                    width="100%"
                    mode="json"
                    name="heatmap-user-data"
                    onChange={handleUserDataChange}
                    value={userData}
                    wrapEnabled={true}
                  />
                </AceEditorWrapper>
              )}
            </InfoContainer>
          </>
        )}
        {selectedPreview === options.dataFeeds.value && (
          <>
            <StyledInfoBanner>{DATA_FEED_BANNER_TEXT}</StyledInfoBanner>
            <InfoContainer>
              <DataFeedHeaderContainer>
                <PreviewHeader data-test={DATA_TEST_PREVIEW_HEADER_DATA_FEEDS}>
                  Data feeds
                </PreviewHeader>
                <PreviewHeaderSubtitle>
                  {dataFeedNames.length} data{' '}
                  {dataFeedNames.length === 1 ? 'feed has' : 'feeds have'} been added
                </PreviewHeaderSubtitle>
              </DataFeedHeaderContainer>

              <AceEditorWrapper hasError={!isValidJson(dataFeedContent)}>
                <AceEditor
                  width="100%"
                  mode="json"
                  name="heatmap-data-feed"
                  value={dataFeedContent}
                  wrapEnabled={true}
                  readOnly
                />
              </AceEditorWrapper>
            </InfoContainer>
          </>
        )}
      </PreviewContent>
    </PreviewContainer>
  );
};
